import { type IPurchase } from "@/interfaces/api/purchase.interface"
import { PurchaseItem } from "@/models/purchase-item.model"
import { ModelFactory } from "@/factories/model-factory"
import { Transaction } from "@/models/transaction.model"
import { Sale } from "@/models/sale.model"
import { IncomingInvoicePurchase } from "@/models/incoming-invoice-purchase.model.ts"

export class Purchase extends Transaction implements IPurchase {
  public static className = "Purchase" // Used for socket communication.
  public static verboseName = "Inkoop"

  purchaseItem: PurchaseItem
  associatedSale?: Sale | null
  incomingInvoicePurchases: IncomingInvoicePurchase[]
  invoiceReference?: string

  constructor(purchase: IPurchase, managed = false) {
    super(purchase, managed)

    this.purchaseItem = ModelFactory.childModel(PurchaseItem, purchase.purchaseItem, managed)
    this.associatedSale = ModelFactory.childModel(Sale, purchase.associatedSale, managed)
    this.incomingInvoicePurchases = ModelFactory.childModels(
      IncomingInvoicePurchase,
      purchase.incomingInvoicePurchases,
      managed
    )
    this.invoiceReference = purchase.invoiceReference
  }

  public getItem() {
    return this.purchaseItem
  }

  public getMatchedQuantity() {
    return this.incomingInvoicePurchases.reduce(
      (sum, purchase) => sum + Number(purchase.quantity),
      0
    )
  }

  /**
   * Returns the signed outstanding quantity (not absolute).
   *
   * The sign of the result follows the original quantity:
   * - Positive quantity → positive outstanding means a positive amount still to match
   * - Negative quantity → negative outstanding means a negative amount still to match
   */
  public getOutstandingQuantity(): number {
    return this.getQuantity() - this.getMatchedQuantity()
  }

  /**
   * Returns true if this purchase is not fully matched.
   *
   * "Outstanding" means the matched quantity is not exactly equal
   * to the original quantity (still something left).
   *
   * Examples:
   *   quantity   matched   outstanding
   *   --------   -------   ------------
   *     10          6         true
   *     10         10         false
   *     10         12         true
   *    -10         -6         true
   *    -10         -10        false
   *    -10         -12        true
   */
  public isOutstanding(): boolean {
    return (
      this.getQuantity() !== this.getMatchedQuantity() &&
      // Consider invoices matched using the legacy mechanism (this.invoiceReference) to not be outstanding.
      !this.invoiceReference
    )
  }

  public getQuantity(): number {
    return parseFloat(this.quantity ?? "0")
  }
}
